import React from 'react';
import PropTypes from 'prop-types';
import { flexRender } from '@tanstack/react-table';
import { Icon } from '@iconify/react';

/**
 * Responsive Data List Table Component
 *
 * Generic table component that renders TanStack Table data.
 * Designed to work with Preline/Tailwind styling.
 * This component is completely agnostic to data structure.
 *
 * @param {Object} props
 * @param {Object} props.table - TanStack Table instance
 * @param {Function} [props.renderExpandedContent] - Function to render expanded row content
 * @param {Function} [props.onRowClick] - Optional callback when clicking a row (receives row.original)
 * @param {Function} [props.getRowProps] - Optional function to get custom props for a row (receives row)
 *
 * @example
 * const table = useReactTable({...});
 * <ResponsiveDataListTable table={table} />
 */
export function ResponsiveDataListTable({ table, renderExpandedContent, onRowClick, getRowProps }) {
  return (
    <table
      className="min-w-full bg-app"
      style={{
        tableLayout: 'auto',
      }}
    >
      <thead>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => {
              const canSort = header.column.getCanSort();
              const isSorted = header.column.getIsSorted();
              const size = header.column.columnDef.size;
              const minSize = header.column.columnDef.minSize;
              const maxSize = header.column.columnDef.maxSize;

              return (
                <th
                  key={header.id}
                  className="px-6 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider border-b border-border-default"
                  onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                  style={{
                    cursor: canSort ? 'pointer' : 'default',
                    userSelect: canSort ? 'none' : 'auto',
                    width: size ? `${size}px` : undefined,
                    minWidth: minSize ? `${minSize}px` : undefined,
                    maxWidth: maxSize ? `${maxSize}px` : undefined,
                  }}
                >
                  <div className="flex items-center gap-2">
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                    {/* Sorting indicator */}
                    {canSort && (
                      <span className={`transition-opacity ${!isSorted && 'opacity-30'}`}>
                        {isSorted === 'asc' && (
                          <Icon icon="lucide:arrow-up" className="size-4" />
                        )}
                        {isSorted === 'desc' && (
                          <Icon icon="lucide:arrow-down" className="size-4" />
                        )}
                        {!isSorted && (
                          <Icon icon="lucide:arrow-up-down" className="size-4" />
                        )}
                      </span>
                    )}
                  </div>
                </th>
              );
            })}
          </tr>
        ))}
      </thead>
      <tbody>
        {table.getRowModel().rows.map((row, rowIndex) => {
          // Get custom row props if provided
          const customRowProps = getRowProps ? getRowProps(row) : {};
          const customClassName = customRowProps.className || '';
          const isLastRow = rowIndex === table.getRowModel().rows.length - 1;

          return (
            <React.Fragment key={row.id}>
              {/* Main row */}
              <tr
                className={`group ${customClassName} ${(onRowClick || row.getCanExpand?.()) ? 'cursor-pointer' : ''}`}
                {...customRowProps}
                onClick={(e) => {
                  // Only trigger if clicking outside of interactive elements
                  const isInteractiveElement = e.target.closest('button, a, [role="button"], input, select, textarea');
                  if (isInteractiveElement) return;

                  // Priority 1: If row can expand, toggle expand
                  if (row.getCanExpand?.()) {
                    row.getToggleExpandedHandler()();
                  }
                  // Priority 2: Otherwise call onRowClick
                  else if (onRowClick) {
                    onRowClick(row.original);
                  }
                }}
              >
                {row.getVisibleCells().map((cell, index) => (
                  <td
                    key={cell.id}
                    className={`px-6 py-4 text-sm text-subtle transition-all overflow-hidden ${row.getIsExpanded() ? '' : 'group-hover:bg-brand/5 group-hover:shadow-none'} ${(onRowClick || row.getCanExpand?.()) ? 'cursor-pointer' : ''} ${index === 0 ? 'group-hover:rounded-l-xl' : ''} ${index === row.getVisibleCells().length - 1 ? 'group-hover:rounded-r-xl' : ''}`}
                    style={{
                      boxShadow: isLastRow ? 'none' : 'inset 0 -1px 0 0 #e5e7eb',
                    }}
                  >
                    {flexRender(
                      cell.column.columnDef.cell,
                      cell.getContext()
                    )}
                  </td>
                ))}
              </tr>

              {/* Expanded row content */}
              {row.getIsExpanded() && renderExpandedContent && (
                <tr>
                  <td
                    colSpan={row.getVisibleCells().length}
                    className="px-0 py-0 border-b border-border-default"
                  >
                    {renderExpandedContent(row)}
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
}

ResponsiveDataListTable.propTypes = {
  table: PropTypes.object.isRequired,
  renderExpandedContent: PropTypes.func,
  onRowClick: PropTypes.func,
  getRowProps: PropTypes.func,
};
